import { useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  GROCERY_SEED,
  normalizeItemName,
  type ItemCatalogApiItem,
  type ShoppingCategory,
} from "@recipevault/shared";
import { db } from "../../sync/db.js";

export interface ItemSuggestion {
  /** Plain-text display label. NEVER rendered as HTML. */
  displayName: string;
  category: ShoppingCategory;
  /** Last unit the household used for this item, or null for seed-only matches. */
  lastUnit: string | null;
}

const MAX_SUGGESTIONS = 8;

/** Title-case a normalized (lowercase) seed key for display, e.g. "bell pepper" -> "Bell Pepper". */
function prettify(normalizedName: string): string {
  return normalizedName.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Autocomplete suggestions for the add-item box.
 *
 * Merges two sources by `normalizedName`:
 *   1. The household's LEARNED catalog (`db.itemCatalog`, startsWith prefix match).
 *   2. The static `GROCERY_SEED` map (cold-start fallback — never written to Dexie).
 *
 * MERGE RULE: on a name collision the learned catalog entry WINS — the household's
 * own training is authoritative; the seed only fills gaps before anything is learned.
 * Catalog matches are returned first, then seed-only matches, capped at MAX_SUGGESTIONS.
 *
 * Empty / whitespace-only query → no suggestions.
 */
export function useItemAutocomplete(query: string): ItemSuggestion[] {
  const normalizedQuery = normalizeItemName(query);

  const catalogMatches = useLiveQuery(
    () => {
      if (!normalizedQuery) return Promise.resolve<ItemCatalogApiItem[]>([]);
      return db.itemCatalog
        .where("normalizedName")
        .startsWith(normalizedQuery)
        .limit(MAX_SUGGESTIONS)
        .toArray();
    },
    [normalizedQuery],
    [] as ItemCatalogApiItem[],
  );

  return useMemo<ItemSuggestion[]>(() => {
    if (!normalizedQuery) return [];

    const seen = new Set<string>();
    const result: ItemSuggestion[] = [];

    // Learned catalog matches first — they win on collision.
    for (const c of catalogMatches ?? []) {
      if (seen.has(c.normalizedName)) continue;
      seen.add(c.normalizedName);
      result.push({
        displayName: c.displayName,
        category: c.category,
        lastUnit: c.lastUnit,
      });
    }

    // Seed-only matches fill remaining slots (skip any already learned).
    for (const key of Object.keys(GROCERY_SEED)) {
      if (result.length >= MAX_SUGGESTIONS) break;
      if (seen.has(key)) continue;
      if (!key.startsWith(normalizedQuery)) continue;
      seen.add(key);
      result.push({
        displayName: prettify(key),
        category: GROCERY_SEED[key] as ShoppingCategory,
        lastUnit: null,
      });
    }

    return result.slice(0, MAX_SUGGESTIONS);
  }, [normalizedQuery, catalogMatches]);
}
